import React, { useContext, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import mapboxgl, { LngLatBoundsLike, Map } from '@adac-mobile/adac-maplibre-gl';
import {
	ILabel,
	IOfferContext,
	ITollSectionOffer,
	ITypeVignetteOrderingProcessFields,
	OfferContext,
	VignetteContext,
} from '@adac-mp/shared';
import { RouteSelection } from '../RouteSelection';

const nameMap = {
	'A13 Brenner Autobahn': 'A 13 Brenner Autobahn',
	'A10 Tauern Autobahn': 'A 10 Tauern Autobahn',
	'S16 Arlberg Schnellstraße': 'S 16 Arlberg Schnellstraße',
	'A11 Karawanken Autobahn': 'A 11 Karawanken Autobahn',
	'A13 Brenner Motorway': 'A 13 Brenner Autobahn',
	'A10 Tauern Motorway': 'A 10 Tauern Autobahn',
	'S16 Arlberg Expressway': 'S 16 Arlberg Schnellstraße',
	'A11 Karawanken Motorway': 'A 11 Karawanken Autobahn',
};

interface ITollSectionMapProps {
	labels: ILabel[];
	contentfulLabels: ITypeVignetteOrderingProcessFields;
}

export const TollSectionMap = ({ labels, contentfulLabels }: ITollSectionMapProps) => {
	const { tollSectionOffers } = useContext<IOfferContext>(OfferContext);
	const { selectedSectionTolls, addSelectedSectionTolls, deleteSelectedSectionTolls } = useContext(VignetteContext);
	const mapContainer = useRef<HTMLDivElement | null>(null);
	const map = useRef<Map | null>(null);
	const [hoveredTollSection, setHoveredTollSection] = useState<ITollSectionOffer | undefined>(undefined);

	const filteredTollSections = tollSectionOffers
		.filter((offer) => offer.moduleTypeId === 3)
		.filter(
			(section, index) =>
				index ===
				tollSectionOffers.findIndex(
					//this can be updated once serviceCatalogNames match
					(obj) => section.serviceCatalogName.slice(0, 10) === obj.serviceCatalogName.slice(0, 10),
				),
		);

	const initializePopups = (mapboxMap: Map) => {
		// delete old popups if extisting
		const popups = document.getElementsByClassName('mapboxgl-popup');
		Array.from(popups).forEach((popup) => {
			popup.remove();
		});

		// generate new popups
		const tollSectionCoordinates = {
			ATA13N: {
				coordinates: [11.453309, 47.35644911443463],
				anchor: 'bottom',
			},
			ATA10N: {
				coordinates: [13.71926032118327, 47.23277382514789],
				anchor: 'top-left',
			},
			ATS16N: {
				coordinates: [10.04285338609775, 47.06899701897012],
				anchor: 'top',
			},
			ATA11N: {
				coordinates: [14.115454184177082, 46.76654148051949],
				anchor: 'top-left',
			},
			ATA9BN: {
				coordinates: [14.449499990231004, 47.75764822418253],
				anchor: 'top-left',
			},
		};

		filteredTollSections.forEach((section) => {
			const placeholder = document.createElement('div');
			const correspondingTollSections = tollSectionOffers?.filter(
				(offer) => offer.serviceCatalogName.slice(0, 10) === section?.serviceCatalogName.slice(0, 10),
			);

			const root = createRoot(placeholder);
			root.render(
				<RouteSelection
					displayOnMap
					route={section}
					correspondingTollSections={correspondingTollSections}
					selectedVignetteTollSections={selectedSectionTolls}
					labels={labels}
					contentfulLabels={contentfulLabels}
					addSelectedVignetteTollSections={addSelectedSectionTolls}
					removeSelectedVignetteTollSectionsById={deleteSelectedSectionTolls}
					setHoveredTollSection={setHoveredTollSection}
				/>,
			);
			new mapboxgl.Popup({
				closeOnClick: false,
				closeButton: false,
				className: 'absolute',
				maxWidth: 'none',
				anchor: tollSectionCoordinates[section.serviceCatalogCode].anchor,
			})
				.setLngLat(tollSectionCoordinates[section.serviceCatalogCode].coordinates)
				.setDOMContent(placeholder)
				.addTo(mapboxMap);
		});
	};

	// generate match expresion which tollRoute is currently selected
	const getMatchExpression = () => {
		const matchExpression: string[] = [];

		// Calculate color values for each country based on 'hdi' value
		for (const row of filteredTollSections) {
			// Convert the range of data values to a suitable color
			const match = selectedSectionTolls?.find((toll) =>
				row.serviceCatalogName.startsWith(toll.serviceCatalogName),
			);

			if (match) {
				if (match.serviceCatalogName.includes('A9')) {
					switch (match.sectionName) {
						case 'Bosruck':
							matchExpression.push('A 9 Pyhrn Autobahn Bosrucktunnel');
							break;
						case 'Gleinalm':
							matchExpression.push('A 9 Pyhrn Autobahn Gleinalmtunnel');
							break;
						case 'Bosruck und Gleinalm' || 'Bosruck and Gleinalm':
							matchExpression.push('A 9 Pyhrn Autobahn Bosrucktunnel');
							matchExpression.push('A 9 Pyhrn Autobahn Gleinalmtunnel');
							break;
						default:
							break;
					}
				} else {
					matchExpression.push(nameMap[row.serviceCatalogName]);
				}
			}
		}

		// // Last value is the default, used where there is no data
		matchExpression.push('default');
		return matchExpression;
	};

	const setHoveredFilter = (mapboxMap: Map) => {
		const matchExpression: string[] = [];

		if (hoveredTollSection) {
			if (hoveredTollSection.serviceCatalogName.includes('A9')) {
				matchExpression.push('A 9 Pyhrn Autobahn Bosrucktunnel');
				matchExpression.push('A 9 Pyhrn Autobahn Gleinalmtunnel');
			} else {
				matchExpression.push(nameMap[hoveredTollSection.serviceCatalogName]);
			}
		}
		matchExpression.push('default');

		mapboxMap.setFilter('charges-at-hovered', ['match', ['get', 'section_name_de'], matchExpression, true, false]);
	};

	const initializeMap = (mapboxMap: Map) => {
		// grey toll routes
		mapboxMap.addLayer({
			id: 'charges-at',
			type: 'line',
			source: 'charges',
			'source-layer': 'charges',
			paint: {
				'line-color': '#707070',

				'line-width': 12,
				'line-opacity': 1,
			},
			layout: {},
			filter: ['all', ['==', 'country_code', 'AT'], ['has', 'section_name_de']],
		});

		// seleted toll routes
		mapboxMap.addLayer({
			id: 'charges-at-selected',
			type: 'line',
			source: 'charges',
			'source-layer': 'charges',
			paint: {
				'line-color': '#15A0D3',
				'line-width': 12,
				'line-opacity': 1,
			},
			layout: {},
			filter: ['all', ['==', 'country_code', 'AT'], ['has', 'section_name_de']],
		});

		// hovered popup matching toll routes
		mapboxMap.addLayer({
			id: 'charges-at-hovered',
			type: 'line',
			source: 'charges',
			'source-layer': 'charges',
			paint: {
				'line-color': '#15A0D3',
				'line-width': 12,
				'line-opacity': 1,
			},
			layout: {},
			filter: ['all', ['==', 'country_code', 'AT'], ['has', 'section_name_de']],
		});

		setHoveredFilter(mapboxMap);

		mapboxMap.setFilter('charges-at-selected', [
			'match',
			['get', 'section_name_de'],
			getMatchExpression(),
			true,
			false,
		]);

		initializePopups(mapboxMap);
	};

	useEffect(() => {
		if (map.current) return;
		if (process.env.MAPBOX_KEY && process.env.MAPBOX_URL && mapContainer?.current) {
			const bounds: LngLatBoundsLike = [
				[8.502707791764891, 45.96482929470618], // Southwest coordinates
				[18.22111019579151, 49.10059646526756], // Northeast coordinates
			];
			mapboxgl.apimKey = process.env.MAPBOX_KEY;

			// eslint-disable-next-line no-new
			map.current = new mapboxgl.Map({
				container: mapContainer.current, // ID of the target div
				style: `${process.env.MAPBOX_URL}/mapbox/map/styles/charges-background/style.json`, // style URL
				center: [13.358024740220856, 47.8562], // starting position [lng, lat]
				zoom: 6.5, // starting zoom
				maxZoom: 8,
				minZoom: 5,
				maxBounds: bounds,
			});

			map.current.on('load', () => {
				if (map.current) {
					map.current.addSource('charges', {
						type: 'vector',
						url: `${process.env.MAPBOX_URL}/mapbox/map/data/charges.json`,
					});

					// overlay over other countries
					map.current.addLayer({
						id: 'charges-countries',
						type: 'fill',
						source: 'charges',
						'source-layer': 'country',
						paint: {
							'fill-opacity': 0.36,
							'fill-color': '#000000',
						},
						filter: ['all', ['!=', 'country_code', 'AT']],
					});

					initializeMap(map.current);
				}
			});
		} else {
			// eslint-disable-next-line no-console
			console.error('Missing Mapbox Keys');
		}
	}, []);

	useEffect(() => {
		// update map data after selection
		if (map.current && map.current.isStyleLoaded()) {
			map.current.setFilter('charges-at-selected', [
				'match',
				['get', 'section_name_de'],
				getMatchExpression(),
				true,
				false,
			]);

			initializePopups(map.current);
		}
	}, [selectedSectionTolls]);

	// link popup and toll section route on map for hover
	useEffect(() => {
		if (map.current && map.current.isStyleLoaded()) {
			setHoveredFilter(map.current);
		}
	}, [hoveredTollSection]);

	return (
		<div
			ref={mapContainer}
			className="relative w-full cursor-grab rounded overflow-hidden h-[474px] md:h-[566px]"
		/>
	);
};
